'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiClockLight,
  PiListNumbersLight,
  PiMusicNotesLight,
  PiTargetLight,
} from 'react-icons/pi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useStatsSummary } from '@/hooks/useStatsSummary';
import { useIsUserAuthorized } from '@/utils/authHelpers';
import {
  formatDuration,
  formatAccuracy,
  formatNumber,
} from '@/utils/statsFormatters';
import { Skeleton } from '@/components/ui/skeleton';

interface StatsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StatsModal = ({ open, onOpenChange }: StatsModalProps) => {
  const { t } = useTranslation();
  const isUserAuthorized = useIsUserAuthorized();
  const { summary, loading, error } = useStatsSummary(isUserAuthorized);

  const stats = [
    {
      icon: PiClockLight,
      value: formatDuration(summary?.totalPracticeTimeMs || 0),
      label: t('profile.stats.total_time', 'Time'),
      color: 'text-blue-500',
    },
    {
      icon: PiListNumbersLight,
      value: formatNumber(summary?.totalSessions || 0),
      label: t('profile.stats.sessions', 'Sessions'),
      color: 'text-green-500',
    },
    {
      icon: PiMusicNotesLight,
      value: formatNumber(summary?.totalNotes || 0),
      label: t('profile.stats.notes', 'Notes'),
      color: 'text-purple-500',
    },
    {
      icon: PiTargetLight,
      value: formatAccuracy(summary?.accuracy || 0),
      label: t('profile.stats.accuracy', 'Accuracy'),
      color: 'text-orange-500',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs sm:max-w-sm p-4">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-center text-base">
            {t('stats_modal.title', 'Your Statistics')}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="grid grid-cols-2 gap-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-3 text-sm text-muted-foreground">
            {t('stats_modal.error', 'Failed to load statistics')}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {stats.map((stat, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 p-3 rounded-lg bg-muted/40"
              >
                <stat.icon size={22} className={stat.color} />
                <div className="flex flex-col min-w-0">
                  <span className="text-lg font-bold leading-tight">
                    {stat.value}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {stat.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatsModal;
